import React, { useRef } from "react";
import Image from "next/image";
import { useRouter } from "next/router";
import Button, { BUTTON_VARIANT } from "@/components/Common/Button";
import ContactUsModalBtn from "@/components/Modal/ContactUs";
import { handlePricingCtaClick } from "@/utils/pricingCta";
import { ArrowRightWhite16Icon } from "@/public/assets/index";
import CaseStudyStats from "./CaseStudyStats";
import styles from "./CaseStudyBanner.module.scss";

/**
 * Case study banner (`cpd_case_study`). The CTA reuses the page's
 * sample/quote flow (same as About Product): pricing redirect when the product
 * has a price, otherwise the Request Sample/Quote modal.
 *
 * `content` (CMS HTML) is not rendered — the design has no slot for it. If it is
 * ever rendered, sanitize it first (utils/faqHubAnswerHtml.js sanitizer).
 * Renders nothing when there is no case study.
 */
const CaseStudyBanner = ({ caseStudy, productData }) => {
  const router = useRouter();
  const quoteModalRef = useRef(null);

  if (!caseStudy?.title) return null;

  const { id, chemicalName, Brand_Name: brandName, hasPrice, cpdGradeMasterId, v2ProductId, v2GradeId } =
    productData;

  const handleCtaClick = () => {
    handlePricingCtaClick({
      hasPrice,
      ctaType: hasPrice ? "reveal_price" : "request_sample_quote",
      cpdGradeMasterId,
      v2ProductId,
      v2GradeId,
      chemicalName,
      brandName,
      router,
      onOpenModal: () => quoteModalRef.current?.setOpen(true),
    });
  };

  // Plain block wrapper: ContactUsModalBtn leaves an empty <div> in flow, which
  // would otherwise become an extra flex/grid item (and an extra gap) in the parent.
  return (
    <div>
      <section className={styles.banner}>
        <div className={styles.body}>
          <div className={styles.copy}>
            {/* TODO(backend): Figma's eyebrow carries industry + country
                ("Case study · Detergents, Türkiye"); the API has no field for it yet. */}
            <p className={styles.eyebrow}>Case study</p>
            <div className={styles.quote}>
              <h2 className={styles.title}>“{caseStudy.title}”</h2>
              {caseStudy.summary && <p className={styles.summary}>{caseStudy.summary}</p>}
            </div>
            <CaseStudyStats stats={caseStudy.metrics ?? null} />
          </div>
          <Button
            variant={BUTTON_VARIANT.FILLED}
            onClick={handleCtaClick}
            className={styles.cta}
            trailingIcon={
              <Image src={ArrowRightWhite16Icon} alt="" width={16} height={16} className={styles.ctaIcon} />
            }
          >
            {hasPrice ? "Sign Up & View Price" : "Request a Sample/Quote"}
          </Button>
        </div>

        {caseStudy.image_url && (
          <div className={styles.media}>
            {/* CMS-hosted image: host isn't in next.config images.domains */}
            <Image
              src={caseStudy.image_url}
              alt=""
              fill
              sizes="(min-width: 1024px) 337px, 100vw"
              unoptimized
              className={styles.mediaImg}
            />
          </div>
        )}
      </section>

      <ContactUsModalBtn
        ref={quoteModalRef}
        heading="Request Sample/Quote"
        service="1"
        subHeading="Fill in the details required to get the sample/quote."
        additionalInformationInitialValue={`Hi Elchemy Team,\nI'm interested in this product, could you please help?\nProduct Name: ${chemicalName}\nQuantity:\nDestination:\nApplication:\nLooking forward`}
        productId={id}
        isUsVersion
      />
    </div>
  );
};

export default CaseStudyBanner;
